import throttle from 'lodash/throttle';

export const ACTIVITY_EVENTS = [
  'click',
  'mousemove',
  'keydown',
  'DOMMouseScroll',
  'mousewheel',
  'mousedown',
  'touchstart',
  'touchmove',
  'focus',
];

export type ActivityCallback = () => void;

export interface ActivityOptions {
  frequency?: number;
  strategy?: 'trailing' | 'leading';
}

export type ActivityFn = (() => void) & { lastCall?: number; timer?: NodeJS.Timeout };

class Activity {
  static callbacks: ([
    () => void,
    ActivityFn,
    ActivityOptions,
  ])[] = [];

  static config: ActivityOptions & { activityEvents: string[] } = {
    activityEvents: ACTIVITY_EVENTS,
    frequency: 200,
    strategy: 'leading',
  };

  static watching = false;

  static watch(callback: () => void, { frequency = this.config.frequency, strategy = this.config.strategy }: ActivityOptions = {}) {
    if (!this.watching) {
      this.config.activityEvents.forEach((eventName) => window.addEventListener(eventName, this.handleActivityEvent));
      this.watching = true;
    }

    const fn: ActivityFn = (strategy === 'leading') ?
      throttle(callback, frequency, { trailing: false }) :
      () => {
        const timeSinceCalled = Date.now() - fn.lastCall || 0;

        if (timeSinceCalled >= frequency) {
          callback();
          fn.lastCall = Date.now();
        } else if (!fn.timer) {
          fn.timer = setTimeout(() => {
            callback();
            fn.lastCall = Date.now();
            fn.timer = null;
          }, frequency - timeSinceCalled);
        }
      };

    this.callbacks.push([callback, fn, { frequency }]);

    return () => this.unwatch(callback);
  }

  static unwatch(callback: ActivityCallback) {
    const index = this.callbacks.findIndex(([fn]) => fn === callback);

    if (index !== -1) {
      this.callbacks.splice(index, 1);
    }

    if (this.watching && !this.callbacks.length) {
      this.config.activityEvents.forEach((eventName) => window.removeEventListener(eventName, this.handleActivityEvent));
      this.watching = false;
    }
  }

  static handleActivityEvent = () => {
    this.callbacks.forEach(([, throttledCallback]) => {
      throttledCallback();
    });
  };
}

export default Activity;
